import React from 'react';
import { Clock, Check, Save, CheckCircle, Loader2 } from 'lucide-react';

interface WizardLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle: string;
  timeEstimate?: string;
  showAutoSave?: boolean;
  isSaving?: boolean;
  lastSaved?: Date | null;
  onManualSave?: () => void;
  headerAction?: React.ReactNode;
}

/**
 * Shared layout component for all wizard steps
 * Provides consistent styling across the 7-step claim creation process
 */
export const WizardLayout: React.FC<WizardLayoutProps> = ({
  children,
  title,
  subtitle,
  timeEstimate,
  showAutoSave = true,
  isSaving = false,
  lastSaved,
  onManualSave,
  headerAction
}) => {
  // Format last saved time
  const formatLastSaved = () => {
    if (!lastSaved) return '';
    const seconds = Math.floor((new Date().getTime() - lastSaved.getTime()) / 1000);
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
      {/* Card Header */}
      <div className="px-8 pt-8 pb-6 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            <p className="text-gray-500 mt-1 text-sm leading-relaxed max-w-xl">{subtitle}</p>
            
            {timeEstimate && (
              <div className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 bg-gray-50 rounded-lg text-sm text-gray-600 font-medium">
                <Clock className="w-4 h-4" />
                Estimated time: {timeEstimate}
              </div>
            )}
          </div>
          
          {headerAction && (
            <div className="flex-shrink-0">
              {headerAction}
            </div>
          )}
        </div>
      </div>
      
      {/* Card Body */}
      <div className="px-8 py-6">
        {/* Integrated Auto-save indicator with manual save button */}
        {showAutoSave && (
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>
                    Progress saved{lastSaved ? ` ${formatLastSaved()}` : ''}
                  </span>
                </>
              )}
            </div>
            {onManualSave && (
              <button
                onClick={onManualSave}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                Save Now
              </button>
            )}
          </div>
        )}
        
        {children}
      </div>
    </div>
  );
};

/**
 * Standalone Auto-save indicator for steps that don't use full WizardLayout
 */
interface AutoSaveIndicatorProps {
  isSaving?: boolean;
  lastSaved?: Date | null;
  onManualSave?: () => void;
  className?: string;
}

export const AutoSaveIndicator: React.FC<AutoSaveIndicatorProps> = ({
  isSaving = false,
  lastSaved,
  onManualSave,
  className = ''
}) => {
  const formatLastSaved = () => {
    if (!lastSaved) return '';
    const seconds = Math.floor((new Date().getTime() - lastSaved.getTime()) / 1000);
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <div className={`flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg ${className}`}>
      <div className="flex items-center gap-2 text-sm text-gray-600">
        {isSaving ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
            <span>Saving...</span>
          </>
        ) : (
          <>
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>
              Progress saved{lastSaved ? ` ${formatLastSaved()}` : ''}
            </span>
          </>
        )}
      </div>
      {onManualSave && (
        <button
          onClick={onManualSave}
          disabled={isSaving}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          Save Now
        </button>
      )}
    </div>
  );
};

/**
 * Form input wrapper with icon and validation
 */
interface FormInputProps {
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  helperText?: string;
  isValid?: boolean;
  type?: 'text' | 'email' | 'tel' | 'date' | 'number';
  required?: boolean;
}

export const FormInput: React.FC<FormInputProps> = ({
  label,
  icon,
  value,
  onChange,
  placeholder,
  helperText,
  isValid,
  type = 'text',
  required = false
}) => {
  const showValidation = value && value.length > 0;
  
  return (
    <div className="mb-6">
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
          {icon}
        </div>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`
            w-full pl-12 pr-12 py-3.5 
            border-2 rounded-xl text-base text-gray-900
            transition-all duration-200
            bg-gray-50 focus:bg-white
            placeholder:text-gray-400
            ${showValidation && isValid 
              ? 'border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/20' 
              : 'border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
            }
            focus:outline-none
          `}
        />
        {showValidation && isValid && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500">
            <Check className="w-5 h-5" />
          </div>
        )}
      </div>
      {helperText && (
        <p className="mt-1.5 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
};

/**
 * Batch upload CTA banner
 */
interface BatchUploadBannerProps {
  onUploadClick: () => void;
  title?: string;
  description?: string;
  buttonText?: string;
  showNewBadge?: boolean;
}

export const BatchUploadBanner: React.FC<BatchUploadBannerProps> = ({
  onUploadClick,
  title = "Batch Upload Available",
  description = "Have all your tax docs in a ZIP? Upload up to 50 files at once and we'll auto-process them.",
  buttonText = "Upload ZIP",
  showNewBadge = true
}) => {
  return (
    <div className="relative flex items-center justify-between p-5 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mb-6 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-48 h-full opacity-10">
        <div className="absolute top-1/2 right-8 -translate-y-1/2 w-32 h-32 rounded-full border-4 border-white" />
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-24 h-24 rounded-full border-4 border-white" />
      </div>
      
      <div className="relative z-10 flex-1">
        <div className="flex items-center gap-2 mb-1">
          {showNewBadge && (
            <span className="px-2 py-0.5 bg-white/20 rounded text-xs font-bold text-white uppercase tracking-wide">
              New
            </span>
          )}
          <span className="text-white font-semibold">{title}</span>
        </div>
        <p className="text-blue-100 text-sm">{description}</p>
      </div>
      
      <button
        onClick={onUploadClick}
        className="relative z-10 flex items-center gap-2 px-5 py-2.5 bg-white text-blue-600 rounded-lg font-semibold text-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z"/>
        </svg>
        {buttonText}
      </button>
    </div>
  );
};

/**
 * Divider with text
 */
export const Divider: React.FC<{ text?: string }> = ({ text = "or enter manually" }) => {
  return (
    <div className="flex items-center my-6">
      <div className="flex-1 h-px bg-gray-200" />
      <span className="px-4 text-sm text-gray-400 font-medium">{text}</span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
};

/**
 * Button group for navigation
 */
interface ButtonGroupProps {
  onBack?: () => void;
  onNext?: () => void;
  onSave?: () => void;
  nextLabel?: string;
  backLabel?: string;
  showBack?: boolean;
  showSave?: boolean;
  isNextDisabled?: boolean;
  isLoading?: boolean;
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  onBack,
  onNext,
  onSave,
  nextLabel = "Continue",
  backLabel = "Back",
  showBack = true,
  showSave = false,
  isNextDisabled = false,
  isLoading = false
}) => {
  return (
    <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
      <div className="flex gap-3">
        {showBack && onBack && (
          <button
            onClick={onBack}
            className="px-6 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold transition-colors"
          >
            {backLabel}
          </button>
        )}
        {showSave && onSave && (
          <button
            onClick={onSave}
            className="px-6 py-3 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            Save Draft
          </button>
        )}
      </div>
      
      <button
        onClick={onNext}
        disabled={isNextDisabled || isLoading}
        className={`
          flex items-center gap-2 px-8 py-3 rounded-xl font-semibold text-base transition-all duration-200
          ${isNextDisabled || isLoading
            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-600/30'
          }
        `}
      >
        {isLoading ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Processing...
          </>
        ) : (
          <>
            {nextLabel}
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </>
        )}
      </button>
    </div>
  );
};

export default WizardLayout;
